import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateService {
  // Properties & Signals
  /** Milliseconds per day */
  private static readonly MS_PER_DAY = 1000 * 60 * 60 * 24;

  // Getters & Setters
  /**
   * Get if it is Christmas (01.12. - 26.12.)
   */
  get isChristmas(): boolean {
    const date = new Date();
    return date.getMonth() === 11 && date.getDate() < 27;
  }

  // Methods
  /**
   * Check if a date is valid
   * @param date to check
   * @returns true if the date is valid, false otherwise
   */
  public static isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Get the week number of a date
   * @param date to get the week number from
   * @returns the week number
   */
  public static getWeekNumber(date: Date): number {
    if (!DateService.isValidDate(date)) {
      throw new Error('Invalid Date');
    }
    const DAYS_OFFSET = 3;
    const DAYS_IN_WEEK = 7;
    const BASE_WEEKDAY = 6;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + DAYS_OFFSET - ((d.getDay() + BASE_WEEKDAY) % DAYS_IN_WEEK));
    const week1 = new Date(d.getFullYear(), 0, 4);
    return (
      1 +
      Math.round(
        ((d.getTime() - week1.getTime()) / DateService.MS_PER_DAY - DAYS_OFFSET + ((week1.getDay() + BASE_WEEKDAY) % DAYS_IN_WEEK)) /
          DAYS_IN_WEEK
      )
    );
  }

  /**
   * Will check if the current date is between the given dates
   * @param from wich date to check
   * @param until wich date to check
   * @param date to check
   * @returns true if the date is between the given dates, false otherwise
   */
  public isBetween(from: Date | null | undefined, until: Date | null | undefined, date: Date = new Date()): boolean {
    const isFutureStart = from && new Date(from) > date;
    const isPastEnd = until && new Date(until) < date;
    const isInvalidRange = from && until && new Date(from) > new Date(until);
    return !(isFutureStart || isPastEnd || isInvalidRange);
  }

  /**
   * Converts a time string in HH:mm format to minutes since midnight.
   * @param timeString Time string to parse (e.g. "08:30").
   * @returns Minutes since midnight, or -1 when the input is missing or invalid.
   */
  public static getTimeInMinutes(timeString?: string): number {
    if (!timeString) return -1;

    const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return -1;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return -1;

    return hours * 60 + minutes;
  }

  /**
   * Returns a copy of the provided date with the time set to 00:00:00.000.
   * @param date Input date.
   * @returns New `Date` instance at the start of the given day.
   */
  public static toStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /**
   * Returns a copy of the provided date with the time set to 23:59:59.999.
   * @param date Input date.
   * @returns New `Date` instance at the end of the given day.
   */
  public static toEndOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  /**
   * Normalizes a date value to a local-midnight Date using only its year/month/day parts.
   * Avoids the UTC off-by-one issue that occurs when `new Date("yyyy-MM-dd")` parses date-only
   * strings as UTC midnight while the rest of the app treats them as local midnight.
   * @param value Date value as ISO string or Date.
   * @returns A new Date at local midnight of the same calendar day.
   */
  public static toDateOnly(value: string | Date): Date {
    const d = value instanceof Date ? value : new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /**
   * Parses a backend ISO date string (yyyy-MM-dd) as a local date.
   * Appends T00:00:00 to force local-time interpretation instead of UTC midnight.
   * @param value Backend date string (e.g. "2026-03-15").
   * @returns Parsed local date or undefined if input is missing or invalid.
   */
  public static parseBackendDate(value?: string): Date | undefined {
    if (!value) return undefined;
    const parsed = new Date(`${value}T00:00:00`);
    return DateService.isValidDate(parsed) ? parsed : undefined;
  }

  /**
   * Parses a backend datetime string (yyyy-MM-ddTHH:mm:ss) as an absolute instant.
   * Backend datetimes are sent in UTC without a timezone designator. JavaScript parses
   * such date-time strings as LOCAL time, so a 'Z' is appended to force UTC interpretation
   * when no offset is present (e.g. "2026-06-24T11:05:05" → 11:05 UTC, not local time).
   * Strings that already carry a 'Z' or numeric offset are parsed as-is.
   * @param value Backend datetime string (e.g. "2026-06-24T11:05:05").
   * @returns Parsed Date at the correct UTC instant or undefined if input is missing or invalid.
   */
  public static parseBackendDateTime(value?: string): Date | undefined {
    if (!value) return undefined;
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const parsed = new Date(hasTimezone ? value : `${value}Z`);
    return DateService.isValidDate(parsed) ? parsed : undefined;
  }

  /**
   * Formats a backend datetime as a wall clock time in the given timezone.
   * Backend datetimes carry no timezone designator but are stored in UTC, so the value is
   * parsed via `parseBackendDateTime` before formatting.
   * @param value Backend datetime string (e.g. "2026-10-21T12:00:00").
   * @param locale Locale used for formatting.
   * @param timeZone IANA timezone to render in; the visitor timezone when omitted.
   * @returns Localized time string (e.g. "14:00") or undefined if the input is missing or invalid.
   */
  public static formatBackendTime(value: string | null | undefined, locale: string, timeZone?: string): string | undefined {
    const date = DateService.parseBackendDateTime(value ?? undefined);
    if (!date) return undefined;

    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone
    }).format(date);
  }

  /**
   * Checks if the given date is still considered "new"
   * within the specified number of days.
   * @param dateInput - The date to check (string or Date).
   * @param days - Number of days the item should be considered new.
   * @returns true if the date is within the last X days, otherwise false.
   */
  public isWithinLastDays(dateInput?: string | Date, days: number = 10): boolean {
    if (!dateInput) return false;
    const inputDate = new Date(dateInput).getTime();
    if (isNaN(inputDate)) return false;
    const now = Date.now();
    const diffInDays = (now - inputDate) / DateService.MS_PER_DAY;
    return diffInDays >= 0 && diffInDays < days;
  }

  /**
   * Formats a date as `yyyy-MM-dd` in local time.
   * @param date The date to format.
   * @returns The formatted date.
   */
  public static toDateString(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  /**
   * Reads a `yyyy-MM-dd` day out of a raw value, rejecting anything else. Useful for form controls
   * that are only ever written by a calendar widget in that exact shape, so any other value (e.g. a
   * culture-formatted backend prefill) is no real selection.
   * @param value The raw value to check.
   * @returns The value unchanged, or an empty string when it is not a `yyyy-MM-dd` date.
   */
  public static asDateOnlyString(value: unknown): string {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
  }

  /**
   * @returns The current month and year.
   */
  public static currentMonth(): { month: number; year: number } {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  }

  /**
   * Formats a month and year as a `yyyy-MM` prefix.
   * @param year The year.
   * @param month The month, 1-based.
   * @returns The formatted prefix.
   */
  public static toYearMonthString(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}`;
  }
}
